import React from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import { useInstitutionPolicy } from '@/shared/hooks/useInstitutionPolicy';
import { renderMarkdown } from '@/shared/utils/markdown';

/**
 * The capital-equipment guidance of the poster's institution.
 *
 * Threshold, text and link all come from that institution's policy, so a
 * coordinator's edit reaches this notice without a deploy. The text goes
 * through renderMarkdown, the security boundary for anything an admin types.
 * The grant-funds carve-out is part of the policy text rather than hardcoded
 * here, so an institution that rewrites its text owns that sentence too.
 */
function CapitalEquipmentNotice({ institution }) {
    const { policy, loading, error } = useInstitutionPolicy(institution);
    const guidance = policy && policy.capitalEquipment;

    return (
        <Alert variant="warning" className="afe-capital-notice">
            <Alert.Heading as="h2" className="h6">
                Capital equipment
            </Alert.Heading>
            {loading && (
                <p className="mb-0" role="status">
                    Loading your institution&apos;s guidance.
                </p>
            )}
            {error && (
                <p className="mb-0">
                    Could not load your institution&apos;s capital equipment
                    guidance. Check with its property office before the item
                    moves.
                </p>
            )}
            {guidance && (
                <>
                    <p className="mb-1">
                        Items valued at $
                        {policy.capitalThresholdUsd.toLocaleString()} or more
                        are tracked assets. Transferring one between departments
                        is allowed and encouraged, but it must still be recorded
                        with your institution before it moves.
                    </p>
                    <div
                        className="mb-1"
                        dangerouslySetInnerHTML={{
                            __html: renderMarkdown(guidance.text),
                        }}
                    />
                    {guidance.link && (
                        <a
                            href={guidance.link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {guidance.link.label}
                        </a>
                    )}
                </>
            )}
        </Alert>
    );
}

CapitalEquipmentNotice.propTypes = { institution: PropTypes.string };

export default CapitalEquipmentNotice;
